import React from 'react';
import { canViewResultsPDF, OrderReviewActionButton, PayBillAction, ViewResultsAction } from '../../components/ActionButtons';
import EndoPlusCollectionSummary from '../../sample-collection/EndoPlusCollection';

const RequisitionStatus = ({
	gearOrder,
	requisition,
	omsRequisition,
	canEditReq,
	userType,
	showRequisition,
	editingReq,
	editingSection,
	editSection,
	invoiceBalanceDue
}) => {
	const requisitionActions = [];
	let accessionStatus = null;
	let requisitionStatus = getRequisitionStatus(gearOrder, requisition);

	let statusText = null;
	switch (requisitionStatus) {
		case Status.ActivationIncomplete:
			if (userType !== 'prc' || gearOrder?.physicianInTheLoop) {
				// Don't show "Activation Incomplete" to practitioners or for Connect orders
				requisitionStatus = null;
			} else if (canEditReq && userType === 'prc' && !requisition.dateLastSampleCollected) {
				statusText = <EndoPlusCollectionSummary orderableItem={omsRequisition?.orderableItem} gearRequisition={requisition} includeInstructions={true} />
			}

			break;
		case Status.ResultsReady:
		// falls through
		case Status.ReceivedByLab:
		// falls through
		case Status.DiscardedSpecimens:
			requisitionStatus = null;
			statusText = null;
			accessionStatus = (
				<AccessionStatus
					gearOrder={gearOrder}
					requisition={requisition}
					userType={userType}
					invoiceBalanceDue={invoiceBalanceDue}
					accessionDetail={requisition?.accessionDetails?.[0]}
				/>
			);
			break;
		case Status.MultiAccession:
			requisitionStatus = null;
			statusText = null;
			accessionStatus = (
				<MultiAccessionStatus
					gearOrder={gearOrder}
					requisition={requisition}
					userType={userType}
					invoiceBalanceDue={invoiceBalanceDue}
				/>
			);
			break;
		default:
			break;
	}

	// Check for empty content so we don't add an empty div with margins (adding uneeded whitespace to the page)
	if (
		!(
			accessionStatus ||
			requisitionStatus ||
			statusText ||
			requisitionActions?.length
		)
	) {
		return null;
	}

	return (
		<div className="mb-20" id={`req-actions_${requisition.id}`}>
			{requisitionStatus && <h4>{requisitionStatus}</h4>}
			{statusText && <div className="mb-5">{statusText}</div>}
			{accessionStatus}
			{requisitionActions}
		</div>
	);
};

// Loop through all accession details and display the status for each
const MultiAccessionStatus = ({ requisition, invoiceBalanceDue, ...props }) => {
	const requisitionStatuses = requisition?.accessionDetails?.map((accessionDetail, index) => {
		const { accessionNo, productName } = accessionDetail;

		/**
		 * TODO: We're only fetching a balance for the first accession on the order so that's the only
		 * one we can show a Pay Bill button for. This needs to be improved to fetch a balance for each
		 * accession.
		 */
		const balanceDue = index === 0 ? invoiceBalanceDue : null;

		return (
			<div key={accessionNo}>
				<h3>
					{productName}
					<div className="small">Order#: {accessionNo}</div>
				</h3>
				<AccessionStatus
					requisition={requisition}
					invoiceBalanceDue={balanceDue}
					accessionDetail={accessionDetail}
					{...props}
				/>
			</div>
		);
	});

	return <>{requisitionStatuses}</>;
};

const AccessionStatus = ({ gearOrder, requisition, userType, invoiceBalanceDue, accessionDetail }) => {
	if (!accessionDetail) {
		return null;
	}
	const { accessionNo } = accessionDetail;

	const requisitionActions = [];
	let requisitionStatus = getRequisitionStatus(gearOrder, requisition, accessionNo);

	let statusText = null;
	let dueDate = null;
	switch (requisitionStatus) {
		case Status.ResultsReady:
			statusText = 'Your results are now available!';
			requisitionActions.push(
				<ViewResultsAction
					key="viewResults"
					gearOrder={gearOrder}
					accessionNo={accessionNo}
					button={<OrderReviewActionButton title="View Results" colorClass="btn-success me-2" />}
				/>
			);

			if (userType === 'prc' && invoiceBalanceDue) {
				requisitionActions.push(
					<PayBillAction
						key="payBill"
						gearOrderId={gearOrder.id}
						gearReqId={requisition.id}
						omsRequisitionId={requisition.reqNumber}
						accessionNo={requisition.accessionNo}
						dateOfBirth={gearOrder.patientInfo?.dateOfBirth}
						button={<OrderReviewActionButton title="Pay Bill" colorClass="me-2" />}
					/>
				);
			}
			break;
		case Status.ReceivedByLab:
			// SCR-8392: Do not show ETC when client has "No Pt Result Emails" (CMS attribute 48)
			if (accessionDetail?.dueDate && !gearOrder.practitioner?.cmsAttributes?.includes(48)) {
				dueDate = new Date(accessionDetail.dueDate)?.toLocaleDateString();
			}
			statusText =
				"We've received your samples and have begun testing. If you need to update your order, please contact us at 800-522-4762.";
			break;
		case Status.DiscardedSpecimens:
			statusText =
				'Our lab was unable to complete your test using the specimens provided.';
			// SCR-9427: Only show "View your Results" message to patients that can view the PDF
			if (canViewResultsPDF({ gearOrder})) {
				statusText += ' View your Results for more information about what went wrong.';
			}
			requisitionActions.push(
				<ViewResultsAction
					key="viewResults"
					gearOrder={gearOrder}
					accessionNo={accessionNo}
					button={<OrderReviewActionButton title="View Results" colorClass="btn-success me-2" />}
				/>
			);
			break;

		default:
			break;
	}

	return (
		<>
			{requisitionStatus && <h4 style={{marginBottom: 0}}>{requisitionStatus}</h4>}
			{(dueDate && (dueDate >= new Date().toLocaleDateString())) && (<small style={{marginTop: 0, marginBottom: 0}}>Your results are expected on or before <b>{dueDate}</b></small>)}
			{statusText && <p style={{marginTop: '10px'}} className="mb-5">{statusText}</p>}
			{requisitionActions}
		</>
	);
};

// All possible status values
const Status = Object.freeze({
	ActivationComplete: 'Activation Complete',
	ActivationIncomplete: 'Activation Incomplete',
	ReceivedByLab: 'Testing in Progress',
	ResultsReady: 'Results Ready',
	Canceled: 'Your order has been Canceled',
	DiscardedSpecimens: 'There was a problem with your specimens',
	MultiAccession: 'Multiple orders'
});

/**
 * Determine the requisition's status based on details of the order
 */
const getRequisitionStatus = (gearOrder, gearRequisition, accessionNo) => {
	if (gearOrder.trackingInformation?.status === 'CANCELED') {
		return Status.Canceled;
	}

	if (!!gearRequisition.accessionNo) {
		if (!accessionNo && gearRequisition?.accessionDetails?.length > 1) {
			return Status.MultiAccession;
		}

		// Filter the details list if we're looking for a specific accession
		let accessionDetails = gearRequisition?.accessionDetails;
		if (accessionNo) {
			accessionDetails = gearRequisition?.accessionDetails?.filter(
				(accessionDetail) => accessionDetail?.accessionNo === accessionNo
			);
		}

		if (
			accessionDetails?.some(
				(accessionDetail) =>
					accessionDetail?.discardedSpecimen && (!accessionNo || accessionNo === accessionDetail.accessionNo)
			)
		) {
			return Status.DiscardedSpecimens;
		}

		/**
		 * Status table
			ID   Status        Display Status
			=    ==========    ==============
			1    Incomplete    Received
			2    Complete      Report Review
			3    Printed       Printed
			4    Reviewed      Released
			5    PITLReview    Phy. Review
			6    Received      Received
			7    Error         Report Generation
			8    QCReview      QC Review
			9    Shipped       Shipped
		*/
		// Status 3, 4, or 9 are 'Released'
		if (
			accessionDetails?.[0]?.requisitionStatusId === 3 ||
			accessionDetails?.[0]?.requisitionStatusId === 4 ||
			accessionDetails?.[0]?.requisitionStatusId === 9
		) {
			return Status.ResultsReady;
		}
		return Status.ReceivedByLab;
	}

	if (!gearRequisition.billingMethod || !gearRequisition.dateLastSampleCollected) {
		return Status.ActivationIncomplete;
	}

	return null;
};

export const isResultsReady = (gearOrder, gearRequisition) => {
	return getRequisitionStatus(gearOrder, gearRequisition, gearRequisition?.accessionNo) === Status.ResultsReady;
};

export default RequisitionStatus;
